// KitchenScreen.tsx
import { Box, Grid, Typography, Stack, Divider, Chip, IconButton, Tooltip } from '@mui/material';
import RefreshIcon from '@mui/icons-material/Refresh';
import React, { useCallback, useMemo, useState } from 'react';
import { BillType, ORDER_TYPE, TAKEAWAY_STATUS } from '../../all-types';
import { useAppDispatch, useAppSelector } from '../../redux-store/hooks';
import { getBills, getTableList } from '../../services';
import { getDayTimestamps } from '../../utils';
import { FullMenuTableCard, TakeawayCard } from './components';
import { useSocketEvent } from '../../realtime';

type TabValue = 'all' | 'dineIn' | 'takeaway';

const KitchenOngoingScreen: React.FC = () => {
  const [selectedTab, setSelectedTab] = useState<TabValue>('all'); // reserved for future use
  const [takeawayBills, setTakeawayBills] = useState<BillType[]>([]);
  const [isRefreshing, setIsRefreshing] = useState(false);
  const [refreshInterval, setRefreshInterval] = useState<number>(10); // Default 10 seconds
  const allTables = useAppSelector((state) => state.preloadData.tables);
  const dispatcher = useAppDispatch();
  const [checkedItems, setCheckedItems] = useState<{ [itemId: string]: boolean }>({}); // reserved

  useSocketEvent<BillType[]>('tables:takeaway', (bills) => {
    setTakeawayBills(bills)
  })

  const pendingTakeAwayBills = useMemo(
    () => takeawayBills?.filter((t) => t.takeAwayStatus !== TAKEAWAY_STATUS.DONE),
    [takeawayBills]
  );

  const fetchData = useCallback(async () => {
    setIsRefreshing(true);
    try {
      const timestamp = getDayTimestamps();
      const response = await getBills(timestamp.morning, timestamp.night);
      if (response?.bills) {
        const ongoingTakeawayBills = response.bills.filter(
          (bill: BillType) => bill?.orderType === ORDER_TYPE.TAKEAWAY
        );
        setTakeawayBills(ongoingTakeawayBills);
      }
      await dispatcher(getTableList());
    } catch (e) {
      console.error('Failed to fetch data:', e);
    } finally {
      setIsRefreshing(false);
    }
  }, [dispatcher]);

  const handleRefresh = () => {
    fetchData();
  };

  // NOTE: auto-refresh remains in place (commented) as provided
  // useEffect(() => {
  //   fetchData(); // Initial fetch
  //   const intervalId = setInterval(() => {
  //     fetchData();
  //   }, refreshInterval * 1000);
  //   return () => clearInterval(intervalId); // Cleanup on unmount
  // }, [fetchData, refreshInterval]);

  const handleTabChange = (_: React.SyntheticEvent, newValue: TabValue) => {
    setSelectedTab(newValue);
  };

  const activeDineInTables = useMemo(
    () =>
      (allTables || []).filter(
        (table) => table.billSession && table.billSession.items && table.billSession.items.length > 0
      ),
    [allTables]
  );

  const dineInCount = activeDineInTables.length;
  const takeawayCount = pendingTakeAwayBills?.length || 0;
  const showEmpty = dineInCount === 0 && takeawayCount === 0;

  return (
    <Box p={2}>
      {/* ORIGINAL CONTROLS (kept exactly, commented): */}
      {/* <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
        <Box display="flex" alignItems="center">
            <Select
                value={refreshInterval}
                onChange={(e) => setRefreshInterval(Number(e.target.value))}
                size="small"
                sx={{ mr: 1, width: '80px' }}
            >
                <MenuItem value={5}>5 sec</MenuItem>
                <MenuItem value={10}>10 sec</MenuItem>
                <MenuItem value={30}>30 sec</MenuItem>
                <MenuItem value={60}>60 sec</MenuItem>
            </Select>

            <Tooltip title="Refresh">
                <IconButton onClick={handleRefresh} disabled={isRefreshing}>
                    <RefreshIcon sx={{ transform: isRefreshing ? 'rotate(360deg)' : 'rotate(0deg)', transition: 'transform 1s' }} />
                </IconButton>
            </Tooltip>
        </Box>
      </Box> */}

      {/* Minimal top bar (non-invasive) */}
      <Stack direction="row" alignItems="center" justifyContent="space-between" mb={2} sx={{ px: 0.5 }}>
        <Stack direction="row" gap={1} alignItems="center">
          <Chip label={`Dine-in (${dineInCount})`} variant="outlined" />
          <Chip label={`Take-away (${takeawayCount})`} variant="outlined" />
        </Stack>
        <Tooltip title="Refresh">
          <span>
            <IconButton
              onClick={handleRefresh}
              disabled={isRefreshing}
              aria-label="refresh"
              sx={{ transform: isRefreshing ? 'rotate(360deg)' : undefined, transition: 'transform 0.8s' }}
            >
              <RefreshIcon />
            </IconButton>
          </span>
        </Tooltip>
      </Stack>

      <Divider sx={{ mb: 2 }} />

      <Grid container spacing={3}>
        {showEmpty && (
          <Grid item xs={12}>
            <Typography
              variant="h6"
              color="text.secondary"
              sx={{ mt: 4, color: 'color15.main', textAlign: 'center', width: '100%' }}
            >
              No orders.
            </Typography>
          </Grid>
        )}

        {/* Dine-in tables */}
        {activeDineInTables.map((table) => (
          <FullMenuTableCard key={table._id} table={table} />
        ))}

        {/* Take-away pending */}
        {pendingTakeAwayBills?.map((order) => (
          <TakeawayCard key={order?._id || order.billNo} order={order} viewOnly={true} />
        ))}
      </Grid>
    </Box>
  );
};

export { KitchenOngoingScreen };
